<template>
  <div class="resource-dashboard">
    <!-- Sidebar Navigation -->
    <aside class="sidebar">
      <nav>
        <ul class="sections">
          <li
            v-for="section in sidebarSections"
            :key="section.heading"
            class="section"
          >
            <div class="section-heading">{{ section.heading }}</div>
            <ul class="tabs">
              <li
                v-for="tab in section.tabs"
                :key="tab.value"
                :class="{ active: selectedTab === tab.value }"
                @click="selectTab(tab.value)"
              >
                {{ tab.label }}
              </li>
            </ul>
          </li>
        </ul>
      </nav>
    </aside>

    <!-- Main Content -->
    <main class="main-content">
      <section v-if="selectedTab === 'dataVariance'" class="dashboard">
        <DataVariance />
      </section>

      <section
        v-if="selectedTab === 'uncertaintyExperiments'"
        class="dashboard"
      >
        <UncertaintyExperiments />
      </section>

      <!-- Fine-Tuning (Training) Tab -->
      <section div v-if="selectedTab === 'fine-tuning'" class="dashboard-grid">
        <div class="training-dashboard">
          <div class="control-panel">
            <ModelControl
              label="Fine-Tuning"
              :status="trainingStatus"
              :endpoints="{
                start: '/training/start',
                pause: '/training/pause',
                resume: '/training/resume',
                stop: '/training/stop',
              }"
              task="training"
              :confidenceIntervals="trainingConfidenceIntervals"
              @update:status="trainingStatus = $event"
              @update:experimentId="trainingExperimentId = $event"
            />
          </div>

          <!-- Training Charts -->
          <div class="training-charts">
            <div class="chart-container">
              <VChart
                :option="trainingLossOption"
                class="chart"
                autoresize
                :updateOptions="{
                  notMerge: false,
                  lazyUpdate: true,
                  replaceMerge: ['series'],
                }"
              />
            </div>
            <div class="chart-container">
              <VChart
                :option="trainingAccF1Option"
                class="chart"
                autoresize
                :updateOptions="{
                  notMerge: false,
                  lazyUpdate: true,
                  replaceMerge: ['series'],
                }"
              />
            </div>
          </div>
        </div>

        <!-- Hyperparameter Summary -->
        <div class="summary-section">
          <div class="chart-container">
            <VChart
              v-if="trainingSummaryOption"
              :option="trainingSummaryOption"
              class="chart"
              autoresize
            />
          </div>
        </div>
      </section>

      <!-- Active Learning Tab -->
      <section v-if="selectedTab === 'active-learning'" class="dashboard-grid">
        <div class="training-dashboard">
          <div class="control-panel">
            <ModelControl
              label="Active Learning"
              :status="alStatus"
              :endpoints="{
                start: '/active-learning/start',
                pause: '/active-learning/pause',
                resume: '/active-learning/resume',
                stop: '/active-learning/stop',
              }"
              task="active-learning"
              @update:status="alStatus = $event"
              @update:experimentId="alExperimentId = $event"
            />
          </div>

          <!-- Active Learning Charts -->
          <div class="ssl-loss">
            <h1>Diversitry and Uncertainty Scores per Iteration</h1>
            <div class="chart-container">
              <VChart :option="alSummaryOption" class="chart" autoresize />
            </div>
            <!-- <div class="chart-container">
              <VChart :option="alScatterOption" class="chart" autoresize />
            </div> -->
            <!-- <div class="chart-container" v-if="alEmbeddingOption">
              <VChart :option="alEmbeddingOption" class="chart" autoresize />
            </div> -->
          </div>
        </div>
        <ActiveLearningDatabaseViewer
          @navigate="(site, sxId, dt) => emitNavigate(site, sxId, dt)"
        />
      </section>

      <!-- Self-Supervised Learning Tab -->
      <section v-if="selectedTab === 'self-supervised'" class="dashboard-grid">
        <div class="training-dashboard">
          <div class="control-panel">
            <ModelControl
              label="Self-Supervised Learning"
              :status="sslStatus"
              :endpoints="{
                start: '/self-supervised/start',
                pause: '/self-supervised/pause',
                resume: '/self-supervised/resume',
                stop: '/self-supervised/stop',
              }"
              task="self-supervised"
              @update:status="sslStatus = $event"
              @update:experimentId="sslExperimentId = $event"
            />
          </div>

          <!-- SSL Charts -->
          <div class="ssl-loss">
            <!-- ▼ running-avg window + pagination controls ▼ -->
            <div
              class="window-control"
              style="
                margin: 0.5em 0;
                display: flex;
                gap: 1em;
                align-items: center;
              "
            >
              <label for="ssl-window"><strong>Avg Window:</strong></label>
              <select id="ssl-window" v-model.number="sslWindowSize">
                <option v-for="w in sslWindowSizes" :key="w" :value="w">
                  {{ w }}
                </option>
              </select>

              <label for="ssl-page-size" style="margin-left: 1.5em"
                ><strong>Page Size:</strong></label
              >
              <select id="ssl-page-size" v-model.number="sslPageSize">
                <option v-for="sz in sslPageSizes" :key="sz" :value="sz">
                  {{ sz }}
                </option>
              </select>

              <label for="ssl-page" style="margin-left: 1.5em"
                ><strong>Page:</strong></label
              >
              <select id="ssl-page" v-model.number="sslPage">
                <option v-for="p in sslPageOptions" :key="p" :value="p">
                  {{ p }}
                </option>
              </select>
            </div>
            <!-- ▲ end window + pagination controls ▲ -->

            <div class="chart-container">
              <VChart
                :option="sslLossOption"
                class="chart"
                autoresize
                :updateOptions="{
                  notMerge: false,
                  lazyUpdate: true,
                }"
              />
            </div>
          </div>
        </div>

        <!-- Hyperparameter Summary -->
        <div class="summary-section">
          <div class="chart-container">
            <VChart
              v-if="sslSummaryOption"
              :option="sslSummaryOption"
              class="chart"
              autoresize
            />
          </div>
        </div>
      </section>

      <section v-if="selectedTab === 'datasets'" class="dashboard">
        <DatasetsManager />
      </section>

      <section v-if="selectedTab === 'scaling'" class="dashboard">
        <ScalingLaws />
      </section>
    </main>
  </div>
</template>

<script>
import { api_config } from "@/env.ts";
import axios from "axios";
import { use } from "echarts/core";
import VChart from "vue-echarts";
import { CanvasRenderer } from "echarts/renderers";
import { LineChart, ParallelChart } from "echarts/charts";
import {
  TitleComponent,
  TooltipComponent,
  GridComponent,
  LegendComponent,
} from "echarts/components";
// import TimeWindowControl from './TimeWindowControl.vue';
// import ResourcingTable from './ResourcingTable.vue';
import { getToken } from "@/keycloak-wrapper.ts";
import { nextTick } from "vue";
import ModelControl from "./ModelControl.vue";
import DatasetsManager from "./DatasetsManager.vue";
import DataVariance from "./DataVariance.vue";
import ScalingLaws from "./ScalingLaws.vue";
import ActiveLearningDatabaseViewer from "./ActiveLearningDatabaseViewer.vue";
import UncertaintyExperiments from "./UncertaintyExperiments.vue";

// Register required ECharts components
use([
  CanvasRenderer,
  LineChart,
  ParallelChart,
  TitleComponent,
  TooltipComponent,
  GridComponent,
  LegendComponent,
]);

export default {
  name: "ResourceDashboard",
  components: {
    VChart,
    // TimeWindowControl,
    // ResourcingTable,
    ModelControl,
    DatasetsManager,
    DataVariance,
    ScalingLaws,
    UncertaintyExperiments,
    ActiveLearningDatabaseViewer,
  },
  data() {
    return {
      // tabs: [
      //   // { label: 'Resourcing', value: 'resourcing' },
      //   { label: "1-1) Data Variance", value: "dataVariance" },
      //   { label: "1-2) Data Variance", value: "uncertaintyExperiments" },
      //   { label: "2) Labelled Datasets", value: "datasets" },
      //   { label: "3) Self-Supervised", value: "self-supervised" },
      //   { label: "4) Fine-Tuning", value: "fine-tuning" },
      //   { label: "5) Active Learning", value: "active-learning" },
      //   { label: "6) Scaling", value: "scaling" },
      // ],
      sidebarSections: [
        {
          heading: "1) Data Discovery",
          tabs: [
            { label: "Data Variance", value: "dataVariance" },
            {
              label: "Model Uncertainty",
              value: "uncertaintyExperiments",
            },
          ],
        },
        {
          heading: "2) Dataset Building",
          tabs: [{ label: "Labelled Datasets", value: "datasets" }],
        },
        {
          heading: "3) Modelling",
          tabs: [
            { label: "Self-Supervised", value: "self-supervised" },
            { label: "Fine-Tuning", value: "fine-tuning" },
            { label: "Active Learning", value: "active-learning" },
          ],
        },
        {
          heading: "4) Capacity & Scaling",
          tabs: [{ label: "Data & Model Scaling", value: "scaling" }],
        },
      ],
      selectedTab: "datasets",
      token: null,
      currentWindow: { start: null, end: null },
      sessions: [],
      placeholders: {
        inferenceTime: "—",
        runningTime: "—",
        batchSize: "—",
        eta: "—",
      },
      trainingConfig: {
        pretrained_model: "",
        epochs: 10,
        val_size: 0.0,
        warmup_epochs: 4,
        early_stopping: 3,
        base_lr: 0.001,
        min_lr: 0.00001,
        weight_decay: 0.01,
        layer_decay: 0.9,
        drop_path: 0.1,
        freq_mask: 2,
        time_mask: 2,
        gain_aug: 0.5,
        frequency_max: 16000,
        clip_grad: 0,
        finetune_strategy: "retrain",
        classification_threshold: 20,
      },
      sslConfig: {
        epochs: 10,
        early_stopping: 3,
        base_lr: 0.001,
        min_lr: 0.00001,
        weight_decay: 0.01,
        layer_decay: 0.9,
        drop_path: 0.1,
        freq_mask: 2,
        time_mask: 2,
        gain_aug: 0.5,
      },
      trainingStatus: "stopped",
      alStatus: "stopped",
      trainingExperimentId: null,
      alExperimentId: null,
      trainingResults: [],
      alResults: [],
      trainingLossOption: {},
      trainingAccF1Option: {},
      trainingSummaries: [],
      trainingSummaryOption: {},
      trainingConfidenceIntervals: {},
      alSummaryOption: {},
      alScatterOption: {},
      alEmbeddingOption: {},

      /* Self-Supervised state */
      sslExperimentId: null,
      sslResults: [],
      sslStatus: "stopped",
      sslLossOption: {},
      sslAccF1Option: {},
      sslSummaries: [],
      sslSummaryOption: {},
      sslWindowSizes: [1, 5, 10, 20, 50, 100, 200, 1000],
      sslWindowSize: 50,
      sslPageSizes: [1000, 5000, 10_000, 50_000, 100_000],
      sslPageSize: 10_000, // default 10k
      sslTotalCount: 0, // total number of metric rows for this experiment
      sslPage: 0, // default = 0 (newest 10k)

      resources: [],
      selectedResource: "",
      metricsInterval: null,
    };
  },
  async mounted() {
    this.token = await getToken();
    await nextTick();
    await this.fetchSessions();
    this.fetchModelParams();
    this.fetchALMetrics();
    this.fetchTrainingSummaries();
    this.fetchSslSummaries();
  },
  beforeUnmount() {
    clearInterval(this.metricsInterval);
  },
  computed: {
    // Build an array [0, 1, 2, … totalPages-1], where
    // totalPages = ceil(sslTotalCount / sslPageSize).
    sslPageOptions() {
      const pages = Math.ceil(this.sslTotalCount / this.sslPageSize);
      return Array.from({ length: pages }, (_, i) => i);
    },
  },
  methods: {
    selectTab(tab) {
      this.selectedTab = tab;
    },
    humanize(key) {
      return key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
    },
    getStep(key) {
      if (key.includes("lr")) return 0.0001;
      if (key.includes("drop") || key.includes("weight")) return 0.01;
      return 1;
    },
    authHeaders() {
      return {
        "Content-Type": "application/json",
        Authorization: `Bearer ${this.token}`,
      };
    },
    async onWindowChange(window) {
      this.currentWindow = window;
      await this.fetchSessions();
    },
    emitNavigate(site, sxId, dt) {
      this.$emit("navigate", site, sxId, dt);
    },

    async fetchSessions() {
      try {
        const res = await axios.post(
          `${api_config.ml_backend}/resources/sessions`,
          {},
          { headers: this.authHeaders() }
        );
        const data = res.data;
        this.sessions = data.sessions;
        this.resources = [data.resource];
        this.selectedResource = data.resource;
        this.trainingStatus = data.sessions["fine-tuning"].status;
        this.alStatus = data.sessions.active_learning.status;
        this.sslStatus = data.sessions.self_supervised.status;
        this.trainingExperimentId = data.sessions["fine-tuning"].experiment_id;
        this.alExperimentId = data.sessions.active_learning.experiment_id;
        this.sslExperimentId = data.sessions.self_supervised.experiment_id;

        this.fetchTrainingMetrics();
        this.fetchSSLResults();
      } catch (e) {
        console.error("error fetching Sessions:", e);
      }
    },

    async fetchModelParams() {
      try {
        const { data } = await axios.get(
          `${api_config.ml_backend}/training/models/MaxViT-B/params`,
          { headers: this.authHeaders() }
        );
        this.modelParamSpecs = data.parameters;
        this.trainingConfig = {};
        this.modelParamSpecs.forEach((spec) => {
          this.trainingConfig[spec.name] =
            spec.default !== undefined
              ? spec.default
              : spec.type === "string"
              ? ""
              : 0;
        });
      } catch (e) {
        console.error("error fetching model params:", e);
      }
    },

    updateMetricCharts(prefix, data, xKey) {
      // no data → clear charts
      if (!data || !data.length) {
        this[`${prefix}LossOption`] = {};
        this[`${prefix}AccF1Option`] = {};
        return;
      }

      // --- 1) ensure data is oldest→newest by step order ---
      const rawSteps = data.map((d) => d[xKey]);
      if (rawSteps.length > 1 && rawSteps[0] > rawSteps[rawSteps.length - 1]) {
        data = [...data].reverse();
      }

      // build epochs (1,2,3…) and steps AFTER ordering
      const epochs = data.map((_, i) => i + 1);
      const steps = data.map((d) => d[xKey]);

      // ---- 2) SSL moving-average chart ----
      if (prefix === "ssl") {
        const losses = data.map((d) => d.Loss);
        const W = this.sslWindowSize;
        const maEpochs = [];
        const maSteps = [];
        const avgLoss = [];

        for (let i = W - 1; i < losses.length; i++) {
          const slice = losses.slice(i - W + 1, i + 1);
          const mean = slice.reduce((a, b) => a + b, 0) / W;
          maEpochs.push(epochs[i]);
          maSteps.push(steps[i]);
          avgLoss.push(mean);
        }

        this.sslLossOption = {
          tooltip: {
            trigger: "axis",
            formatter: (params) =>
              params
                .map((p) => {
                  const idx = p.dataIndex;
                  const epoch = p.axisValue;
                  const step = maSteps[idx];
                  const y = Array.isArray(p.value) ? p.value[1] : p.value;
                  return [
                    `Epoch: ${epoch}`,
                    `Step: ${step}`,
                    `${p.seriesName}: ${y}`,
                  ].join("<br/>");
                })
                .join("<br/>"),
          },
          xAxis: {
            type: "value",
            name: "Epoch",
            min: maEpochs[0] || 1,
            max: maEpochs[maEpochs.length - 1] || epochs.length,
          },
          yAxis: { type: "value" },
          series: [
            {
              name: `Loss (MA${W})`,
              type: "line",
              smooth: true,
              data: maEpochs.map((e, i) => [e, avgLoss[i]]),
            },
          ],
          legend: { data: [`Loss (MA${W})`] },
          grid: { top: 40, left: 40, bottom: 20, right: 30 },
          dataZoom: [
            { type: "slider", xAxisIndex: 0 },
            { type: "inside", xAxisIndex: 0 },
            { type: "slider", yAxisIndex: 0, left: 0 },
            { type: "inside", yAxisIndex: 0 },
          ],
        };

        return;
      }

      // ---- 3) Non-SSL Loss chart ----
      const lossSeries = [
        {
          name: "Loss",
          type: "line",
          smooth: true,
          data: epochs.map((e, i) => [e, data[i].Loss]),
        },
      ];

      if (data[0].ValidLoss !== undefined) {
        lossSeries.push({
          name: "Validation Loss",
          type: "line",
          smooth: true,
          lineStyle: { type: "dashed" },
          data: epochs.map((e, i) => [e, data[i].ValidLoss]),
        });
      }

      this[`${prefix}LossOption`] = {
        tooltip: {
          trigger: "axis",
          formatter: (params) => {
            if (!params.length) return "";
            // all params share the same epoch and step
            const idx = params[0].dataIndex;
            const epoch = params[0].axisValue;
            const step = steps[idx];
            // build lines
            const lines = [`Epoch: ${epoch}`, `Step: ${step}`];
            // then each series
            params.forEach((p) => {
              const y = Array.isArray(p.value) ? p.value[1] : p.value;
              lines.push(`${p.seriesName}: ${y}`);
            });
            return lines.join("<br/>");
          },
        },
        xAxis: {
          type: "value",
          name: "Epoch",
          min: epochs[0],
          max: epochs[epochs.length - 1],
        },
        yAxis: { type: "value" },
        series: lossSeries,
        legend: { data: lossSeries.map((s) => s.name) },
        grid: { top: 40, left: 40, bottom: 20, right: 30 },
        dataZoom: [
          { type: "slider", xAxisIndex: 0 },
          { type: "inside", xAxisIndex: 0 },
          { type: "slider", yAxisIndex: 0, left: 0 },
          { type: "inside", yAxisIndex: 0 },
        ],
      };

      // ---- 4) Non-SSL Accuracy & F1 chart ----
      const accF1Series = [
        {
          name: "Accuracy",
          type: "line",
          yAxisIndex: 0,
          smooth: true,
          data: epochs.map((e, i) => [e, data[i].Accuracy]),
        },
        {
          name: "F1",
          type: "line",
          yAxisIndex: 1,
          smooth: true,
          data: epochs.map((e, i) => [e, data[i].F1]),
        },
      ];

      if (data[0].ValidAccuracy !== undefined) {
        accF1Series.push({
          name: "Validation Accuracy",
          type: "line",
          yAxisIndex: 0,
          smooth: true,
          lineStyle: { type: "dashed" },
          data: epochs.map((e, i) => [e, data[i].ValidAccuracy]),
        });
      }
      if (data[0].ValidF1 !== undefined) {
        accF1Series.push({
          name: "Validation F1",
          type: "line",
          yAxisIndex: 1,
          smooth: true,
          lineStyle: { type: "dashed" },
          data: epochs.map((e, i) => [e, data[i].ValidF1]),
        });
      }

      this[`${prefix}AccF1Option`] = {
        tooltip: {
          trigger: "axis",
          formatter: (params) => {
            if (!params.length) return "";
            // all params share the same epoch and step
            const idx = params[0].dataIndex;
            const epoch = params[0].axisValue;
            const step = steps[idx];
            // build lines
            const lines = [`Epoch: ${epoch}`, `Step: ${step}`];
            // then each series
            params.forEach((p) => {
              const y = Array.isArray(p.value) ? p.value[1] : p.value;
              lines.push(`${p.seriesName}: ${y}`);
            });
            return lines.join("<br/>");
          },
        },
        legend: { data: accF1Series.map((s) => s.name) },
        xAxis: {
          type: "value",
          name: "Epoch",
          min: epochs[0],
          max: epochs[epochs.length - 1],
        },
        yAxis: [
          {
            type: "value",
            name: "Accuracy (%)",
            min: 0,
            max: 100,
            position: "left",
          },
          {
            type: "value",
            name: "F1 Score",
            min: 0,
            max: 1.0,
            position: "right",
          },
        ],
        series: accF1Series,
        grid: { top: 40, left: 40, bottom: 20, right: 30 },
        dataZoom: [
          { type: "slider", xAxisIndex: 0 },
          { type: "inside", xAxisIndex: 0 },
          { type: "slider", yAxisIndex: 0, left: 0 },
          { type: "inside", yAxisIndex: 0 },
        ],
      };
    },

    /* Training */
    async fetchTrainingMetrics() {
      if (!this.trainingExperimentId) return;
      const { data } = await axios.post(
        `${api_config.ml_backend}/training/${this.trainingExperimentId}/metrics`,
        {},
        { headers: this.authHeaders() }
      );
      this.trainingResults = data.metrics;
      this.trainingStatus = data.status;
      this.trainingConfidenceIntervals = data.confidence_intervals;
      this.updateMetricCharts("training", this.trainingResults, "Step");
    },
    startTrainingMetricsUpdates() {
      clearInterval(this.metricsInterval);
      if (this.trainingStatus === "running") {
        this.fetchTrainingMetrics();
        this.metricsInterval = setInterval(this.fetchTrainingMetrics, 5000);
      }
    },

    /* Active Learning */
    async fetchALMetrics() {
      if (!this.alExperimentId) return;
      const res = await axios.get(
        `${api_config.ml_backend}/active-learning/${this.alExperimentId}/metrics`,
        { headers: this.authHeaders() }
      );
      this.alRaw = res.data.raw;
      this.alSummary = res.data.summary;
      this.alEmbedding = res.data.embedding;
      this.updateALSummaryChart();
      this.updateALScatterChart();
      this.updateALEmbeddingChart();
    },
    startALUpdates() {
      clearInterval(this.metricsInterval);
      if (this.alStatus === "running") {
        this.fetchALMetrics();
        this.metricsInterval = setInterval(this.fetchALMetrics, 5000);
      }
    },
    updateALSummaryChart() {
      const it = this.alSummary.map((d) => d.iteration);
      const unc = this.alSummary.map((d) => d.avg_uncertainty);
      const div = this.alSummary.map((d) => d.avg_diversity);
      this.alSummaryOption = {
        tooltip: { trigger: "axis" },
        legend: { data: ["Avg Uncertainty", "Avg Diversity"] },
        xAxis: { type: "category", data: it },
        yAxis: { type: "value" },
        series: [
          { name: "Avg Uncertainty", type: "line", data: unc, smooth: true },
          { name: "Avg Diversity", type: "line", data: div, smooth: true },
        ],
        grid: { top: 40, left: 40, right: 20, bottom: 30 },
      };
    },
    updateALScatterChart() {
      const grouped = {};
      this.alRaw.forEach((r) => {
        (grouped[r.Iteration] || (grouped[r.Iteration] = [])).push([
          r.AvgUncertaintyScore,
          r.AvgDiversityScore,
        ]);
      });
      this.alScatterOption = {
        tooltip: {},
        legend: { data: Object.keys(grouped).map((i) => `Iter ${i}`) },
        xAxis: { name: "Uncertainty", type: "value" },
        yAxis: { name: "Diversity", type: "value" },
        series: Object.entries(grouped).map(([it, data]) => ({
          name: `Iter ${it}`,
          type: "scatter",
          data,
          symbolSize: 6,
        })),
        grid: { top: 40, left: 40, right: 20, bottom: 30 },
      };
    },
    updateALEmbeddingChart() {
      if (!this.alEmbedding || !this.alEmbedding.X) {
        this.alEmbeddingOption = {};
        return;
      }
      const points = this.alEmbedding.X.map((xy, i) => ({
        value: xy,
        symbolSize: 4,
        itemStyle: {
          color: this.alEmbedding.latest_batch_idxs.includes(i)
            ? "#e74c3c"
            : "#3498db",
        },
      }));
      this.alEmbeddingOption = {
        tooltip: {
          formatter: (p) =>
            `[${p.value[0].toFixed(2)},${p.value[1].toFixed(2)}]`,
        },
        xAxis: { name: "Dim1", type: "value" },
        yAxis: { name: "Dim2", type: "value" },
        series: [{ name: "Embedding", type: "scatter", data: points }],
        grid: { top: 40, left: 40, right: 20, bottom: 30 },
      };
    },

    /* Self-Supervised */
    async fetchSSLCount() {
      if (!this.sslExperimentId) {
        this.sslTotalCount = 0;
        return;
      }
      try {
        const { data } = await axios.get(
          `${api_config.ml_backend}/self-supervised/${this.sslExperimentId}/metrics/count`,
          { headers: this.authHeaders() }
        );
        this.sslTotalCount = data.total;
      } catch (e) {
        console.error("error fetching SSL total count:", e);
        this.sslTotalCount = 0;
      }
    },

    async fetchSSLResults() {
      if (!this.sslExperimentId) {
        this.sslResults = [];
        this.sslLossOption = {};
        return;
      }

      // Calculate limit & offset based on pageSize/page:
      const limit = this.sslPageSize;
      const offset = this.sslPage * this.sslPageSize;

      try {
        const { data } = await axios.get(
          `${api_config.ml_backend}/self-supervised/${this.sslExperimentId}/metrics`,
          {
            params: { limit, offset },
            headers: this.authHeaders(),
          }
        );
        this.sslResults = data.metrics;
        // Re‐compute chart from newly fetched page
        this.updateMetricCharts("ssl", this.sslResults, "Step");
      } catch (e) {
        console.error("error fetching SSL results:", e);
        this.sslResults = [];
        this.sslLossOption = {};
      }
    },
    startSSLMetricsUpdates() {
      clearInterval(this.metricsInterval);
      if (this.sslStatus === "running") {
        this.fetchSSLResults();
        this.metricsInterval = setInterval(this.fetchSSLResults, 5000);
      }
    },

    /* Hyperparameter Summaries */
    async fetchTrainingSummaries() {
      try {
        const { data } = await axios.post(
          `${api_config.ml_backend}/training/summaries`,
          {},
          { headers: this.authHeaders() }
        );
        this.trainingSummaries = data.summaries;
        this.updateTrainingSummaryChart();
      } catch (e) {
        console.error("error fetching training summaries:", e);
      }
    },
    async fetchSslSummaries() {
      try {
        const { data } = await axios.post(
          `${api_config.ml_backend}/self-supervised/summaries`,
          {},
          { headers: this.authHeaders() }
        );
        this.sslSummaries = data.summaries;
        this.updateSslSummaryChart();
      } catch (e) {
        console.error("error fetching ssl summaries:", e);
      }
    },
    generateMagmaColors(steps) {
      if (typeof steps !== "number" || steps < 1) {
        return [];
      }
      const base = [
        "#000004",
        "#1b0c41",
        "#4f0a6d",
        "#781c6d",
        "#a52c60",
        "#cf4446",
        "#ed6925",
        "#fb9b06",
        "#f7d13d",
        "#fcfdbf",
      ];
      if (steps === 1) {
        return [base[Math.floor(base.length / 2)]];
      }
      const parseHex = (hex) => {
        const n = parseInt(hex.slice(1), 16);
        return [(n >> 16) & 0xff, (n >> 8) & 0xff, n & 0xff];
      };
      const stops = base.map(parseHex);

      return Array.from({ length: steps }, (_, i) => {
        const t = i / (steps - 1);
        const x = t * (stops.length - 1);
        const i0 = Math.floor(x),
          i1 = Math.min(i0 + 1, stops.length - 1);
        const f = x - i0;
        const [r0, g0, b0] = stops[i0];
        const [r1, g1, b1] = stops[i1];
        const r = Math.round(r0 + (r1 - r0) * f);
        const g = Math.round(g0 + (g1 - g0) * f);
        const b = Math.round(b0 + (b1 - b0) * f);
        return `rgb(${r},${g},${b})`;
      });
    },
    updateTrainingSummaryChart() {
      // guard missing or empty summaries
      if (
        !Array.isArray(this.trainingSummaries) ||
        this.trainingSummaries.length === 0
      ) {
        this.trainingSummaryOption = {};
        return;
      }

      const sample = this.trainingSummaries[0];
      const axes = Object.keys(sample).filter(
        (k) => typeof sample[k] === "number"
      );
      const f1Dim = axes.indexOf("F1");

      const parallelAxis = axes.map((key, idx) => ({
        dim: idx,
        name: this.humanize(key) /* …styles… */,
      }));

      const dataArr = this.trainingSummaries.map((row) =>
        axes.map((k) => row[k])
      );

      const palette = this.generateMagmaColors(this.trainingSummaries.length);

      this.trainingSummaryOption = {
        title: { text: "Hyperparameter Relationships" },
        tooltip: {
          trigger: "item",
          formatter: function (param) {
            // Extracting relevant values from param
            // console.log('dataIndex', param.dataIndex, 'seriesIndex', param.seriesIndex);
            const experimentId = param.dataIndex + 1; // Assuming experiment ID starts from 1
            const f1Score = param.data[f1Dim]; // Get the F1 score from the data
            // Dimension index cannot be extracted simply
            // const variableName = parallelAxis[param.seriesIndex].name; // Get the name of the variable being looked at
            // const variableValue = param.value[param]; // Get the name of the variable being looked at
            const color = param.color; // Get the color for the current line

            // Format the tooltip string
            // return `
            //   <strong>Experiment ID:</strong> ${experimentId}<br>
            //   <strong>F1 Score:</strong> ${f1Score}<br>
            //   <strong>${variableName}:</strong> ${variableValue}<br>
            //   <strong>Color:</strong> <span style="color:${color};">${color}</span>
            // `;
            return `
              <strong>Experiment ID:</strong> <span style="color:${color};">${experimentId}</span><br>
              <strong>F1 Score:</strong><span style="color:${color};"> ${f1Score}</span><br>
            `;
          },
        },
        legend: { show: false },
        parallelAxis,
        parallel: {
          top: 65,
          bottom: 35,
          left: 40,
          right: 35,
        },
        visualMap: {
          type: "continuous",
          dimension: f1Dim,
          min: 0.0,
          max: 1.0,
          inRange: { color: palette },
          orient: "horizontal",
          left: "center",
          bottom: 0,
          text: ["High F1", "Low F1"],
          formatter: function (value) {
            return value.toFixed(2);
          },
          realtime: false,
        },
        series: [
          {
            name: "Experiment",
            type: "parallel",
            smooth: true,
            progressive: 500,
            inactiveOpacity: 0,
            activeOpacity: 0.01,
            lineStyle: { width: 1.5, opacity: 0.8 },
            data: dataArr,
          },
        ],
      };
    },
    updateSslSummaryChart() {
      // guard missing or empty summaries
      if (!Array.isArray(this.sslSummaries) || this.sslSummaries.length === 0) {
        this.sslSummaryOption = {};
        return;
      }

      const sample = this.sslSummaries[0];
      const axes = Object.keys(sample).filter(
        (k) => typeof sample[k] === "number"
      );
      const lossDim = axes.indexOf("Loss");

      const parallelAxis = axes.map((key, idx) => ({
        dim: idx,
        name: this.humanize(key) /* …styles… */,
      }));

      const dataArr = this.sslSummaries.map((row) => axes.map((k) => row[k]));

      const palette = this.generateMagmaColors(this.sslSummaries.length);

      this.sslSummaryOption = {
        title: { text: "Hyperparameter Relationships" },
        tooltip: {
          trigger: "item",
          formatter: function (param) {
            // Extracting relevant values from param
            // console.log(
            //   "dataIndex",
            //   param.dataIndex,
            //   "seriesIndex",
            //   param.seriesIndex
            // );
            const experimentId = param.dataIndex + 1; // Assuming experiment ID starts from 1
            const f1Score = param.data[lossDim]; // Get the F1 score from the data
            // Dimension index cannot be extracted simply
            // const variableName = parallelAxis[param.seriesIndex].name; // Get the name of the variable being looked at
            // const variableValue = param.value[param]; // Get the name of the variable being looked at
            const color = param.color; // Get the color for the current line

            // Format the tooltip string
            // return `
            //   <strong>Experiment ID:</strong> ${experimentId}<br>
            //   <strong>F1 Score:</strong> ${f1Score}<br>
            //   <strong>${variableName}:</strong> ${variableValue}<br>
            //   <strong>Color:</strong> <span style="color:${color};">${color}</span>
            // `;
            return `
              <strong>Experiment ID:</strong> <span style="color:${color};">${experimentId}</span><br>
              <strong>F1 Score:</strong><span style="color:${color};"> ${f1Score}</span><br>
            `;
          },
        },
        legend: { show: false },
        parallelAxis,
        parallel: {
          top: 65,
          bottom: 35,
          left: 40,
          right: 35,
        },
        visualMap: {
          type: "continuous",
          dimension: lossDim,
          min: 0.0,
          max: 1.0,
          inRange: { color: palette },
          orient: "horizontal",
          left: "center",
          bottom: 0,
          text: ["High F1", "Low F1"],
          formatter: function (value) {
            return value.toFixed(2);
          },
          realtime: false,
        },
        series: [
          {
            name: "Experiment",
            type: "parallel",
            smooth: true,
            progressive: 500,
            inactiveOpacity: 0,
            activeOpacity: 0.01,
            lineStyle: { width: 1.5, opacity: 0.8 },
            data: dataArr,
          },
        ],
      };
    },

    // Styling helper
    getStatusClass(status) {
      if (status === "running") return "status-running";
      if (status === "idle" || status === "paused") return "status-paused";
      return "status-unknown";
    },
  },
  watch: {
    trainingStatus(newStatus) {
      if (newStatus === "running") {
        this.startTrainingMetricsUpdates();
      } else {
        clearInterval(this.metricsInterval);
        this.fetchTrainingSummaries();
      }
    },
    alStatus(newStatus) {
      if (newStatus === "running") {
        this.startALUpdates();
      } else {
        clearInterval(this.metricsInterval);
      }
    },
    sslStatus(newStatus) {
      if (newStatus === "running") {
        this.startSSLMetricsUpdates();
      } else {
        clearInterval(this.metricsInterval);
      }
    },
    sslWindowSize() {
      if (this.sslResults && this.sslResults.length) {
        this.updateMetricCharts("ssl", this.sslResults, "Step");
      }
    },
    sslPageSize() {
      // whenever page size changes, reset to page 0 and re-fetch
      this.sslPage = 0;
      this.fetchSSLCount().then(() => this.fetchSSLResults());
    },
    sslPage() {
      // whenever page changes, re-fetch
      this.fetchSSLResults();
    },
    sslExperimentId() {
      // if a new experiment is selected, reset to page 0 and fetch
      this.sslPage = 0;
      this.sslTotalCount = 0;
      this.fetchSSLCount().then(() => this.fetchSSLResults());
    },
  },
};
</script>

<style scoped>
.resource-dashboard {
  display: flex;
  height: calc(100vh - var(--header-height));
}
.main-content {
  width: 100%;
  height: 100%;
  overflow-y: auto;
  overflow-x: auto;
  padding: 0 !important;
}
.dashboard-grid {
  display: grid;
  width: 100%;
  grid-template-rows: 1fr 1fr;
  gap: var(--spacing-sm);
  height: calc(100vh - var(--header-height));
  min-height: 0;
}
.dashboard {
  display: grid;
  width: 100%;
  grid-template-rows: 1fr;
  gap: var(--spacing-sm);
  height: calc(100vh - var(--header-height));
  min-height: 0;
}
.training-dashboard {
  display: grid;
  width: 100%;
  grid-template-columns: 21.875rem auto;
  gap: var(--spacing-sm);
  height: 100%;
}
.sidebar {
  width: 12.5rem;
  background: var(--colour-navy);
  color: #ecf0f1;
  height: calc(100vh - var(--header-height));
}
.sidebar .section {
  margin-bottom: 1em;
  padding: 0;
}
.sidebar .section-heading {
  padding: 0.5em 1em;
  font-size: 0.9rem;
  font-weight: bold;
  color: #bdc3c7;
  text-transform: uppercase;
}
.sidebar .tabs li {
  padding-left: 1.5em;
  font-size: 0.95rem;
}
.sidebar ul {
  list-style: none;
  padding: 0;
  margin: 0;
}
.sidebar li {
  padding: 1em;
  cursor: pointer;
  text-align: left;
}
.tabs li:hover {
  background: #34495e;
}
.tabs li.active {
  background: #1abc9c;
}
.main-content {
  flex: 1;
  padding: 1em;
  overflow-y: auto;
}
.control-panel {
  border: 1px solid #aaa;
  padding: 0em;
  border-radius: var(--radius-md);
  background: #f9f9f9;
  width: 21.875rem;
  height: 100%;
  justify-content: center;
}
.param-row {
  display: grid;
  grid-template-columns: 200px auto;
  gap: 0.5em;
  align-items: center;
}
.param-row span {
  justify-self: left;
}
input,
select {
  width: 6.25rem;
}
.buttons {
  display: flex;
  gap: 0.5em;
  margin-top: 0.5em;
}
.training-charts {
  background-color: #f9f9f9;
  border-radius: var(--radius-md);
  padding: 0.5em;
  border: 1px solid #aaa;
  height: 100%;
  display: grid;
  grid-template-rows: 1fr 1fr;
  gap: 0.25em;
}
.window-control {
  height: 2em;
}
.ssl-loss {
  background-color: #f9f9f9;
  border-radius: var(--radius-md);
  padding: 0.5em;
  border: 1px solid #aaa;
  height: 100%;
  display: grid;
  grid-template-rows: 2em auto;
  gap: 0.25em;
  min-height: 0;
}
.chart-container {
  background-color: #fcfcfc;
  border-radius: var(--radius-md);
  padding: 0.25em;
  border: 1px solid #aaa;
  height: 100%;
}
.metrics-placeholder {
  margin-top: 1em;
  font-style: italic;
}
.summary-section {
  width: 100%;
  height: 100%;
}
</style>
